/**
 * PipelineStage.jsx — One pipeline stage card.
 * Shows: step number, title, count/total, progress bar, rate, ETA, action buttons.
 */

import { api } from "../api.js";

const STEPS = {
  scrape: { num: "02", label: "Scrape URLs",  desc: "Download + cache text",    cls: "scrape" },
  enrich: { num: "03", label: "Enrich Docs",  desc: "Extract metadata via LLM", cls: "enrich" },
  score:  { num: "04", label: "Heatmap Score",desc: "Score vs 96 questions",     cls: "score"  },
};

export default function PipelineStage({ step, stepData = {}, disabled }) {
  const meta = STEPS[step];
  if (!meta) return null;

  const { done = 0, total = 0, pct = 0, status = "idle", rate = 0, eta_min } = stepData;

  const canStart  = !disabled && ["idle", "stopped", "done"].includes(status) && status !== "running";
  const canPause  = status === "running";
  const canResume = status === "paused";
  const canStop   = status === "running" || status === "paused";

  const handleStart  = () => api.startTask(step).catch(console.error);
  const handlePause  = () => api.pauseTask(step).catch(console.error);
  const handleResume = () => api.resumeTask(step).catch(console.error);
  const handleStop   = () => api.stopTask(step).catch(console.error);

  const etaText = eta_min != null
    ? eta_min < 1 ? "< 1 min" : `~${eta_min.toFixed(0)} min`
    : null;

  return (
    <div className={`hm-stage-card hm-stage-card--${meta.cls}`}>
      <div className="hm-stage-card__header">
        <div className="hm-stage-card__title">
          <span className={`hm-stage-card__step-num hm-step-${meta.cls}`}>{meta.num}</span>
          {meta.label}
        </div>
        <StatusPill status={status} />
      </div>

      <div className="hm-stage-card__counts">
        {done.toLocaleString()}
        <span style={{ fontSize: 14, fontWeight: 400, color: "var(--text-tertiary)" }}>
          {" "}/ {total.toLocaleString()}
        </span>
      </div>
      <div className="hm-stage-card__pct">{pct.toFixed(1)}% — {meta.desc}</div>

      <div className="hm-progress-bar">
        <div
          className={`hm-progress-bar__fill hm-fill-${meta.cls}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>

      <div className="hm-stage-card__meta">
        <span>{rate > 0 ? `${rate} docs/min` : "—"}</span>
        {etaText && <span>ETA {etaText}</span>}
        {status === "done" && <span style={{ color: "#0F7E6D" }}>✓ Complete</span>}
      </div>

      <div className="hm-stage-card__actions">
        {canStart && (
          <button className={`hm-btn hm-btn--primary hm-btn--sm`} onClick={handleStart}>
            ▶ Start
          </button>
        )}
        {canPause && (
          <button className="hm-btn hm-btn--warning hm-btn--sm" onClick={handlePause}>
            ⏸ Pause
          </button>
        )}
        {canResume && (
          <button className="hm-btn hm-btn--success hm-btn--sm" onClick={handleResume}>
            ▶ Resume
          </button>
        )}
        {canStop && (
          <button className="hm-btn hm-btn--ghost hm-btn--sm" onClick={handleStop}>
            ⏹
          </button>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    idle:    { label: "Idle",    cls: "idle" },
    running: { label: "Running", cls: "running" },
    paused:  { label: "Paused",  cls: "paused" },
    done:    { label: "Done",    cls: "done" },
    stopped: { label: "Stopped", cls: "paused" },
  };
  const { label, cls } = map[status] || map.idle;
  return (
    <span className={`hm-topbar__pill hm-topbar__pill--${cls}`} style={{ fontSize: 10, padding: "2px 8px" }}>
      <span className={`hm-status-dot hm-status-dot--${cls}`} />
      {label}
    </span>
  );
}
